import React, { useState } from 'react';
import { BiPlay, BiPause, BiTime, BiPlus, BiEdit } from 'react-icons/bi';
import { AiOutlineFunction } from 'react-icons/ai';

import Bouton from '../../UI/Bouton/Bouton';
import ChampDeTexte from '../../UI/ChampDeTexte/ChampDeTexte';
import Tableau from '../../UI/tableau/Tableau';

import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import S from './ProfilTask.module.scss';

const ProfilTask = ({ taskSelectionne, taskEdition, creer }) => {
  const [filtreNom, setFiltreNom] = useState('');
  const [filtreStatus, setFiltreStatus] = useState('');

  // Mock data pour l'exemple
  const mockTasks = [
    {
      id: 1,
      nom: 'Collecte de données capteurs',
      fonction: 'getData',
      status: 'active',
      frequence: 'Toutes les 30 minutes',
      prochaine_execution: '2025-10-03 15:30:00',
      derniere_execution: '2025-10-03 15:00:00',
      date_creation: '2025-09-15 10:00:00',
      arguments: [
        {
          nom: 'sensors_ids',
          type: 'sensors_list',
          valeur: '[1, 2, 3, 4, 5]',
          obligatoire: true,
        },
        {
          nom: 'date_range',
          type: 'date_range',
          valeur: 'last_24_hours',
          obligatoire: true,
        },
        {
          nom: 'format',
          type: 'text',
          valeur: 'json',
          obligatoire: false,
        },
      ],
    },
    {
      id: 2,
      nom: 'Génération rapport hebdomadaire',
      fonction: 'generateReport',
      status: 'paused',
      frequence: 'Tous les lundis à 09:00',
      prochaine_execution: '2025-10-07 09:00:00',
      derniere_execution: '2025-09-30 09:00:00',
      date_creation: '2025-09-01 08:00:00',
      arguments: [
        {
          nom: 'report_type',
          type: 'select',
          valeur: 'weekly_summary',
          obligatoire: true,
        },
        {
          nom: 'recipients',
          type: 'multi_select',
          valeur: 'admin@example.com, user@example.com',
          obligatoire: true,
        },
      ],
    },
    {
      id: 3,
      nom: 'Synchronisation base de données',
      fonction: 'syncDatabase',
      status: 'active',
      frequence: 'Toutes les heures',
      prochaine_execution: '2025-10-03 16:00:00',
      derniere_execution: '2025-10-03 15:00:00',
      date_creation: '2025-08-20 14:30:00',
      arguments: [
        {
          nom: 'table_name',
          type: 'text',
          valeur: 'sensor_data',
          obligatoire: true,
        },
        {
          nom: 'batch_size',
          type: 'number',
          valeur: '1000',
          obligatoire: false,
        },
      ],
    },
  ];

  // Configuration pour la sélection de lignes
  const selectRow = {
    mode: 'radio',
    clickToSelect: true,
    hideSelectAll: true,
    hideSelectColumn: true,
    style: () => ({
      background: '#eeb214',
      color: 'black',
    }),
    onSelect: (row) => taskSelectionne(row),
  };

  const filteredTasks = mockTasks.filter((task) => {
    const matchesNom = task.nom.toLowerCase().includes(filtreNom.toLowerCase());
    const matchesStatus = !filtreStatus || task.status === filtreStatus;
    return matchesNom && matchesStatus;
  });

  const handleEdit = (e, task) => {
    e.stopPropagation();
    if (taskEdition) {
      taskEdition(task);
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? (
      <span className={`${S.statusBadge} ${S.statusActive}`}>
        <BiPlay size={14} /> Actif
      </span>
    ) : (
      <span className={`${S.statusBadge} ${S.statusPaused}`}>
        <BiPause size={14} /> Pause
      </span>
    );
  };

  const colonnes = [
    {
      dataField: 'nom',
      text: 'Nom de la fonction',
      sort: true,
      style: { width: '30%' },
    },
    {
      dataField: 'fonction',
      text: 'Fonction',
      sort: true,
      style: { width: '20%' },
      formatter: (cell) => (
        <span className={S.functionName}>
          <AiOutlineFunction size={14} /> {cell}
        </span>
      ),
    },
    {
      dataField: 'frequence',
      text: 'Fréquence',
      style: { width: '20%' },
      formatter: (cell) => (
        <span className={S.frequency}>
          <BiTime size={14} /> {cell}
        </span>
      ),
    },
    {
      dataField: 'status',
      text: 'Statut',
      sort: true,
      style: { width: '15%' },
      formatter: (cell) => getStatusBadge(cell),
    },
    {
      dataField: 'prochaine_execution',
      text: 'Prochaine exécution',
      sort: true,
      style: { width: '15%' },
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      style: { width: '10%' },
      formatter: (cell, row) => (
        <div className={S.actionsContainer}>
          <Bouton
            type={type_btn.secondary}
            rond={true}
            taille="small"
            cliquer={(e) => handleEdit(e, row)}
            ajoutClass={S.actionButton}
          >
            <BiEdit size={14} />
          </Bouton>
        </div>
      ),
    },
  ];

  return (
    <div className={S.profilTaskContainer}>
      <div className={S.headerSection}>
        <h2 className={S.title}>Liste des fonctions</h2>
        <Bouton
          type={type_btn.primary}
          cliquer={creer}
          ajoutClass={S.createButton}
        >
          <BiPlus size={18} /> Nouvelle fonction
        </Bouton>
      </div>

      <div className={S.filtersSection}>
        <div className={S.filterRow}>
          <ChampDeTexte
            label="Rechercher par nom"
            placeholder="Filtrer par nom..."
            value={filtreNom}
            onChange={(e) => setFiltreNom(e.target.value)}
            ajoutClass={S.filterInput}
          />
          <div className={S.statusFilter}>
            <label>Statut:</label>
            <select
              value={filtreStatus}
              onChange={(e) => setFiltreStatus(e.target.value)}
              className={S.selectFilter}
            >
              <option value="">Tous</option>
              <option value="active">Actif</option>
              <option value="paused">Pause</option>
            </select>
          </div>
        </div>
      </div>

      <div className={S.tableSection}>
        <Tableau
          cleLigne="id"
          donnees={filteredTasks}
          colonnes={colonnes}
          selectionnerLignes={selectRow}
          noDataIndication="Aucune fonction trouvée"
        />
      </div>
    </div>
  );
};

export default ProfilTask;
